// Idle-based session expiry.
//
// Tracks user activity (mouse, keyboard, scroll, touch) and surfaces
// a warning shortly before forcing a sign-out. Per design
// (`CLOUD_TECHNICAL_DESIGN.md` §3.2): 28-minute warning, 30-minute
// hard cutoff. Numbers come in as props so a future Preferences page
// can offer per-user override.

import { useEffect, useRef, useState } from 'react';

export type IdleState =
  | { phase: 'active' }
  | { phase: 'warning'; expiresInSec: number }
  | { phase: 'expired' };

interface Options {
  /** Total ms of inactivity before forced sign-out. */
  totalMs: number;
  /** Ms before expiry when the warning surfaces. */
  warningLeadMs: number;
  /** Disable the timer (e.g. when not signed in). */
  enabled: boolean;
}

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

/**
 * Hook returning current idle phase. Caller renders the warning modal
 * and reacts to phase==='expired' by triggering OIDC sign-out.
 *
 * Activity listeners are attached to `window` and use `{ passive: true }`
 * so scroll perf isn't affected. The timer logic ticks once a second
 * via setInterval — cheap, and the countdown shown in the modal needs
 * second-level granularity anyway.
 */
export function useIdleTimeout({ totalMs, warningLeadMs, enabled }: Options): IdleState {
  const [state, setState] = useState<IdleState>({ phase: 'active' });
  const lastActivityAtRef = useRef<number>(Date.now());

  // Reset on activity. Cheap event listeners; just stamp the time.
  useEffect(() => {
    if (!enabled) return;
    const onActivity = () => {
      lastActivityAtRef.current = Date.now();
      // If we were warning, reset to active. If expired, stay
      // expired — the SPA is mid-redirect by then.
      setState((prev) => (prev.phase === 'warning' ? { phase: 'active' } : prev));
    };
    for (const ev of ACTIVITY_EVENTS) {
      window.addEventListener(ev, onActivity, { passive: true });
    }
    return () => {
      for (const ev of ACTIVITY_EVENTS) {
        window.removeEventListener(ev, onActivity);
      }
    };
  }, [enabled]);

  // Tick the timer.
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const idleMs = Date.now() - lastActivityAtRef.current;
      if (idleMs >= totalMs) {
        setState({ phase: 'expired' });
        return;
      }
      if (idleMs >= totalMs - warningLeadMs) {
        const expiresInSec = Math.max(0, Math.ceil((totalMs - idleMs) / 1000));
        setState({ phase: 'warning', expiresInSec });
        return;
      }
      setState((prev) => (prev.phase === 'active' ? prev : { phase: 'active' }));
    };
    tick();
    const handle = window.setInterval(tick, 1000);
    return () => window.clearInterval(handle);
  }, [enabled, totalMs, warningLeadMs]);

  return state;
}

/**
 * "Stay signed in" — caller invokes when the user clicks the
 * Dismiss/Continue button in the warning modal. Resets the idle
 * timer to active without requiring an event-style activity gesture.
 * (Just imperatively bumping the timestamp would work too, but
 * exporting this makes the integration point obvious.)
 */
export function resetIdleTimer() {
  // We can't reach into the hook's ref from here without a context.
  // Instead: synthesize an activity event that the hook listens for.
  window.dispatchEvent(new Event('mousemove'));
}
